import * as React from 'react';
import type { DataProvider, I18nProvider, RaThemeOptions } from 'react-admin';
import type { SvgIconComponent } from '@mui/icons-material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CarRepairOutlinedIcon from '@mui/icons-material/CarRepairOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

const FbrSmartWorkspace = React.lazy(() =>
    import('./FbrSmartWorkspace').then(m => ({ default: m.FbrSmartWorkspace }))
);
const AccountingWorkspace = React.lazy(() =>
    import('./AccountingWorkspace').then(m => ({ default: m.AccountingWorkspace }))
);
const SettingsWorkspace = React.lazy(() =>
    import('./SettingsWorkspace').then(m => ({ default: m.SettingsWorkspace }))
);
const AutoDealerWorkspace = React.lazy(() =>
    import('./AutoDealersWorkspace').then(m => ({ default: m.AutoDealersWorkspace }))
);
const FuelingStationWorkspace = React.lazy(() =>
    import('./FuelingStationWorkspace').then(m => ({ default: m.FuelingStationWorkspace }))
);
const PurchasesWorkspace = React.lazy(() =>
    import('./PurchasesWorkspace').then(m => ({ default: m.PurchasesWorkspace }))
);
const AutoWorkshopWorkspace = React.lazy(() =>
    import('./AutoWorkshopWorkspace').then(m => ({ default: m.AutoWorkshopWorkspace }))
);
const InventoryWorkspace = React.lazy(() =>
    import('./InventoryWorkspace').then(m => ({ default: m.InventoryWorkspace }))
);
const PRAWorkSpaceApp = React.lazy(() =>
    import('./PRAWorkSpaceApp').then(m => ({ default: m.PRAWorkSpaceApp }))
);
const SalesWorkspace = React.lazy(() =>
    import('./SalesWorkspace').then(m => ({ default: m.SalesWorkspace }))
);
const ProjectManagementWorkspace = React.lazy(() =>
    import('./ProjectManagementWorkspace').then(m => ({ default: m.ProjectManagementWorkspace }))
);
const DocumentManagementWorkspace = React.lazy(() =>
    import('./DocumentManagementWorkspace').then(m => ({ default: m.DocumentManagementWorkspace }))
);
const PayrollWorkspace = React.lazy(() =>
    import('./PayrollWorkspace').then(m => ({ default: m.PayrollWorkspace }))
);
export type AppRegistryEntry = {
    id: string;
    name: string;
    /** Shown as react-admin `<Admin title>`; defaults to {@link name} when omitted. */
    adminTitle?: string;
    description?: string;
    accentColor?: string;
    /** MUI icon shown in the app center / launcher tiles. */
    icon?: SvgIconComponent;
    disabled?: boolean;
    /** First segment of permission strings for this app (e.g. fbr, accounting, settings). */
    permissionsPrefix: string;
    /** Browser path prefix for this workspace Admin basename. */
    basePath: string;
    /**
     * Renders the workspace shell (`<Admin>` + resources as direct Fragment children).
     * Add a new app by registering here and implementing this component in its own module — no changes to `App.tsx` / `Root.tsx` routing loops.
     */
    WorkspaceComponent: React.ComponentType<WorkspaceComponentProps>;
};

export type WorkspaceComponentProps = {
    theme?: RaThemeOptions;
    lightTheme?: RaThemeOptions;
    darkTheme?: RaThemeOptions;
    i18nProvider: I18nProvider;
    dataProvider: DataProvider;
    /** The registry entry for this workspace (ids, paths, titles, permission prefix). */
    app: AppRegistryEntry;
};

export const ACCOUNTING_SUITE_APP_ID = 'accounting-suite';

export const SETTINGS_APP_ID = 'settings';
export const AUTO_DEALERS_APP_ID = 'auto-dealers';
export const FUELING_STATIONS_APP_ID = 'fueling-stations';
export const PURCHASES_APP_ID = 'purchases';
export const AUTO_WORKSHOP_APP_ID = 'auto-workshop';
export const INVENTORY_APP_ID = 'inventory';
export const PRA_WORKSPACE_APP_ID = 'pra-workspace';
export const SALES_APP_ID = 'sales';
export const PROJECT_MANAGEMENT_APP_ID = 'project-management';
export const DOCUMENT_MANAGEMENT_APP_ID = 'document-management';
export const FBR_SMART_APP_ID = 'fbr-smart';
export const PAYROLL_APP_ID = 'payroll';
export const APPS_REGISTRY: AppRegistryEntry[] = [
    {
        id: 'fbr-smart',
        name: 'FBR Smart',
        adminTitle: 'FBR Smart Application',
        description: 'Invoicing, catalog & company setup',
        accentColor: '#00585C',
        icon: ReceiptLongOutlinedIcon,
        permissionsPrefix: 'fbr',
        basePath: '/fbr',
        WorkspaceComponent: FbrSmartWorkspace,
    },
    {
        id: PRA_WORKSPACE_APP_ID,
        name: 'PRA Smart',
        adminTitle: 'PRA Smart Application',
        description: 'Punjab Revenue Authority digital invoicing',
        accentColor: '#8E1B1B',
        icon: RequestQuoteOutlinedIcon,
        permissionsPrefix: 'pra',
        basePath: '/pra',
        WorkspaceComponent: PRAWorkSpaceApp,
    },
    {
        id: ACCOUNTING_SUITE_APP_ID,
        name: 'Accounting',
        adminTitle: 'Accounting',
        description: 'General ledger, configuration & reporting',
        accentColor: '#6B4FBB',
        icon: AccountBalanceOutlinedIcon,
        permissionsPrefix: 'accounting',
        basePath: '/accounting',
        WorkspaceComponent: AccountingWorkspace,
    },
    {
        id: FUELING_STATIONS_APP_ID,
        name: 'Fueling Station Management',
        adminTitle: 'Fueling Station Management',
        description: 'Configuration, daily entry & dashboard',
        accentColor: '#B26A00',
        icon: LocalGasStationOutlinedIcon,
        permissionsPrefix: 'fuel',
        basePath: '/fuel',
        WorkspaceComponent: FuelingStationWorkspace,
    },
     {
        id: AUTO_DEALERS_APP_ID,
        name: 'Auto Dealers',
        adminTitle: 'Auto Dealers',
        description: 'Sales, Purchase of vehicles',
        accentColor: '#2E7D32',
        icon: DirectionsCarOutlinedIcon,
        permissionsPrefix: 'auto-dealers',
        basePath: '/auto-dealers',
        WorkspaceComponent: AutoDealerWorkspace,
    },
    {
        id: PURCHASES_APP_ID,
        name: 'Purchases',
        adminTitle: 'Purchases',
        description: 'Purchase orders, GRN & warehouse setup',
        accentColor: '#2F6F9E',
        icon: ShoppingCartOutlinedIcon,
        permissionsPrefix: 'purchases',
        basePath: '/purchases',
        WorkspaceComponent: PurchasesWorkspace,
    },
    {
        id: AUTO_WORKSHOP_APP_ID,
        name: 'Auto Workshop',
        adminTitle: 'Auto Workshop',
        description: 'Job cards, sales, billing & workshop reports',
        accentColor: '#1565C0',
        icon: CarRepairOutlinedIcon,
        permissionsPrefix: 'workshop',
        basePath: '/auto-workshop',
        WorkspaceComponent: AutoWorkshopWorkspace,
    },
    {
        id: INVENTORY_APP_ID,
        name: 'Inventory',
        adminTitle: 'Inventory',
        description: 'Stock transactions, issuance & warehouse reports',
        accentColor: '#5D4037',
        icon: Inventory2OutlinedIcon,
        permissionsPrefix: 'inventory',
        basePath: '/inventory',
        WorkspaceComponent: InventoryWorkspace,
    },
    {
        id: SALES_APP_ID,
        name: 'Sales',
        adminTitle: 'Sales',
        description: 'Quotations, sales orders & invoicing',
        accentColor: '#8E24AA',
        icon: SellOutlinedIcon,
        permissionsPrefix: 'sales',
        basePath: '/sales',
        WorkspaceComponent: SalesWorkspace,
    },
    {
        id: PROJECT_MANAGEMENT_APP_ID,
        name: 'Project Management',
        adminTitle: 'Project Management',
        description: 'Projects, WBS, tasks, budgets & milestones',
        accentColor: '#00796B',
        icon: AccountTreeOutlinedIcon,
        permissionsPrefix: 'project',
        basePath: '/project-management',
        WorkspaceComponent: ProjectManagementWorkspace,
    },
    {
        id: PAYROLL_APP_ID,
        name: 'Payroll',
        adminTitle: 'Payroll',
        description: 'Salary structures, contracts & payslips',
        accentColor: '#AD1457',
        icon: PaymentsOutlinedIcon,
        permissionsPrefix: 'hr',
        basePath: '/payroll',
        WorkspaceComponent: PayrollWorkspace,
    },
    {
        id: DOCUMENT_MANAGEMENT_APP_ID,
        name: 'Document Management',
        adminTitle: 'Document Management',
        description: 'Central document browser, categories & audit trail',
        accentColor: '#3d7a7a',
        icon: FolderCopyOutlinedIcon,
        permissionsPrefix: 'dms',
        basePath: '/documents',
        WorkspaceComponent: DocumentManagementWorkspace,
    },
    {
        id: SETTINGS_APP_ID,
        name: 'Settings',
        adminTitle: 'Settings',
        description: 'Security groups & access control',
        accentColor: '#2E7D32',
        icon: SettingsOutlinedIcon,
        permissionsPrefix: 'settings',
        basePath: '/settings',
        WorkspaceComponent: SettingsWorkspace,
    },
];

export const DEFAULT_ACTIVE_APP_ID = 'fbr-smart';

export function appRegistryEntryById(id: string): AppRegistryEntry | undefined {
    return APPS_REGISTRY.find(a => a.id === id);
}

export function workspaceRootPath(activeAppId: string): string {
    return appRegistryEntryById(activeAppId)?.basePath ?? '/fbr';
}

/** URL wins on first paint (store may still be default until SyncActiveAppFromPath runs). */
export function resolveAppIdFromPathname(pathname: string): string | null {
    for (const app of APPS_REGISTRY) {
        const base = app.basePath;
        if (pathname === base || pathname.startsWith(`${base}/`)) return app.id;
    }
    return null;
}
